/**
 * Export to Excel (.xlsx) for admin screens.
 */

import * as XLSX from "xlsx";
import type { StudentForAdmin, StudentPaymentRecord, TransactionRecord } from "../types";

const COLUMNS = [
  "Student Name",
  "Parent Name",
  "Parent Email",
  "Class Name",
  "Month",
  "Amount",
  "Payment Status",
  "Payment Date",
] as const;

function formatPaidAt(paidAt: TransactionRecord["paidAt"]): string {
  if (!paidAt) return "";
  const sec = "seconds" in paidAt && paidAt.seconds != null ? paidAt.seconds : (paidAt as { _seconds?: number })._seconds;
  if (sec == null || typeof sec !== "number") return "";
  const d = new Date(sec * 1000);
  return Number.isNaN(d.getTime()) ? "" : d.toISOString().slice(0, 10);
}

/**
 * Export Pagos screen transactions to Excel.
 * File name: pagos_{month}_{YYYY-MM-DD}.xlsx
 */
export function exportTransactionsToExcel(
  data: TransactionRecord[],
  options?: { month?: string }
): void {
  const headers = ["Estudiante", "Monto", "Método", "Origen", "Fecha"];
  const rows = data.map((t) => [
    t.studentName || "—",
    t.amount ?? 0,
    t.paymentMethod || "—",
    t.source === "manual" ? "Manual" : "En línea",
    formatPaidAt(t.paidAt ?? null),
  ]);
  const ws = XLSX.utils.aoa_to_sheet([headers, ...rows]);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "Pagos");
  const month = options?.month ?? "";
  const safeMonth = month.replace(/[^0-9-]/g, "") || "export";
  const date = new Date().toISOString().slice(0, 10);
  const fileName = `pagos_${safeMonth}_${date}.xlsx`;
  XLSX.writeFile(wb, fileName);
}

/**
 * Export Estudiantes screen list to Excel.
 * File name: estudiantes_{month}_{YYYY-MM-DD}.xlsx
 */
export function exportEstudiantesToExcel(
  data: StudentForAdmin[],
  options?: { month?: string }
): void {
  const headers = ["Nombre", "ID"];
  const rows = data.map((s) => [s.name ?? "—", s.id]);
  const ws = XLSX.utils.aoa_to_sheet([headers, ...rows]);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "Estudiantes");
  const month = options?.month ?? "";
  const safeMonth = month.replace(/[^0-9-]/g, "") || "todos";
  const date = new Date().toISOString().slice(0, 10);
  const fileName = `estudiantes_${safeMonth}_${date}.xlsx`;
  XLSX.writeFile(wb, fileName);
}

/**
 * Export data to Excel and trigger download.
 * File name: pagos_{className}_{month}_{YYYY-MM-DD}.xlsx
 */
export function exportStudentsToExcel(
  data: StudentPaymentRecord[],
  options?: { className?: string; month?: string }
): void {
  const rows = data.map((r) => [
    r.studentName,
    r.parentName,
    r.parentEmail,
    r.className,
    r.month,
    r.amount,
    r.paymentStatus,
    r.paymentDate,
  ]);
  const ws = XLSX.utils.aoa_to_sheet([[...COLUMNS], ...rows]);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "Pagos");
  const className = options?.className ?? "export";
  const month = options?.month ?? "";
  const safeClass = className.replace(/[^a-zA-Z0-9-_]/g, "_").slice(0, 40);
  const safeMonth = month.replace(/[^0-9-]/g, "") || "all";
  const date = new Date().toISOString().slice(0, 10);
  const fileName = `pagos_${safeClass}_${safeMonth}_${date}.xlsx`;
  XLSX.writeFile(wb, fileName);
}
